import classnames from "classnames";
import * as I from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";

import {trackEvent} from "metrics";
import {isDataLoaded} from "utils";

import api from "v2/api";
import {apiAssignee, getAssignmentsOptions} from "v2/api/Assignees";
import {apiError} from "v2/api/Errors";
import {AppData, Route} from "v2/api/Records";

import AssignmentsSelect from "v2/components/AssignmentsSelect";
import FieldError from "v2/components/FieldError";
import FieldGroup from "v2/components/FieldGroup";
import RouteLink from "v2/components/RouteLink";
import ServerError from "v2/components/ServerError";

import ModalForm from "v2/components/responsive/ModalForm";

export default class EditRouteForm extends Component {
  static contextTypes = {
    appData: PropTypes.instanceOf(AppData),
    dateData: ImmutablePropTypes.listOf(Route),
    location: PropTypes.object,
    onUpdateDateData: PropTypes.func,
    onUpdateRouteData: PropTypes.func,
    params: PropTypes.shape({
      date: PropTypes.string.isRequired,
      routeId: PropTypes.string.isRequired,
    }),
    routeData: PropTypes.instanceOf(Route),
    router: PropTypes.object,
  }
  static propTypes = {
    modal: PropTypes.bool,
    onCancel: PropTypes.func,
  }

  constructor(props, context) {
    super(props, context);

    const {
      routeData: {assignments, id, manpower},
    } = context;

    this.state = {
      assignments: I.List(getAssignmentsOptions(assignments, id)),
      data: null,
      dataError: null,
      errors: I.Map(),
      manpower,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  handleAssignmentsChange = (value) => {
    this.setState({
      assignments: I.List(value),
    });
  }

  handleManpowerChange = (evt) => {
    this.setState({
      manpower: evt.target.value,
    });
  }

  handleSubmit = (evt) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {
      location: {query},
      onUpdateDateData,
      onUpdateRouteData,
      params: {date, routeId},
      routeData,
      router,
    } = this.context;
    const {modal, onCancel} = this.props;
    const {assignments, manpower} = this.state;

    api()
      .patch(this.apiUrl, {
        assignments: assignments.map(item => item.value),
        manpower,
      })
      .then(() => {
        onUpdateRouteData(null, () => {
          if (modal) {
            onCancel();
          } else {
            router.push({
              pathname: `/${date}/${routeId}`,
              query,
            });
          }
          trackEvent("Edit Route", ["Route", "edit", routeId], {
            Date: date,
            ID: routeId,
            Manpower: manpower,
            Type: routeData.type,
          });
          onUpdateDateData();
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  get apiUrl() {
    const {params: {routeId}} = this.context;
    return `/routes/${routeId}`;
  }

  loadData() {
    const {params: {date}} = this.context;

    api()
      .request({
        method: "options",
        url: this.apiUrl,
        params: {date},
      })
      .then(({data}) => {
        this.setState({
          data: I.List(data.assignments.map(apiAssignee)),
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            dataError: apiError(err.response.data),
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  renderReassignAlert() {
    const {dateData, routeData: {id: routeId}} = this.context;
    const {assignments, data} = this.state;
    if (!data) {
      return null;
    }

    const alreasyBusyAssignees = assignments
      .filter(({value}) => data.find(({id}) => id === value))
      .map(({value}) => {
        return data.find(({id}) => id === value);
      })
      .filter(({assignedTo}) => {
        return assignedTo && assignedTo !== routeId;
      })
      .map(assignee => {
        const assignedToRoute = dateData.find(({id}) => id === assignee.assignedTo);
        return (
          <li key={`assignee-${assignee.id}`}>
            <strong>{assignee.fullName || assignee.email}</strong> will be
            removed from rote ID: {assignee.assignedTo}, <RouteLink route={assignedToRoute} />
          </li>
        );
      });

    if (!alreasyBusyAssignees.size) {
      return null;
    }

    return (
      <div className="alert alert-warning">
        <p>
          The following gardeners will be removed from their existing
          routes and reassigned to this route:
        </p>
        <br />
        <ul>{alreasyBusyAssignees}</ul>
      </div>
    );
  }

  renderContent() {
    const {routeData: {id: routeId}} = this.context;
    const {assignments, data, dataError, errors, manpower} = this.state;

    if (dataError) {
      return <ServerError data={dataError} />;
    }

    const labelClassName = "col-xs-12 col-sm-3 control-label";
    const fieldContainerClassName = "col-xs-12 col-sm-9";

    let nonFieldErrors;
    if (errors.get("nonFieldErrors")) {
      nonFieldErrors = (
        <div className={classnames(fieldContainerClassName, "col-sm-offset-3", "has-error")}>
          <FieldError>{errors.get("nonFieldErrors")}</FieldError>
        </div>
      );
    }

    return (
      <div className="form-horizontal">
        <AssignmentsSelect
          data={data}
          error={errors.get("assignments")}
          fieldContainerClassName={fieldContainerClassName}
          focus
          labelClassName={labelClassName}
          onChange={this.handleAssignmentsChange}
          routeId={routeId}
          selected={assignments} />

        <FieldGroup error={errors.get("manpower")}>
          <label className={labelClassName}>Manpower:</label>
          <div className={fieldContainerClassName}>
            <input
              className="form-control"
              onChange={this.handleManpowerChange}
              type="text"
              value={manpower} />
            <FieldError>{errors.get("manpower")}</FieldError>
          </div>
        </FieldGroup>

        {nonFieldErrors}

        {this.renderReassignAlert()}
      </div>
    );
  }

  render() {
    const {submitted} = this.state;
    return (
      <ModalForm
        btnSubmitDisabled={submitted || !isDataLoaded(this.state)}
        btnSubmitLabel="Edit"
        modalTitle="Edit Route"
        onSubmit={this.handleSubmit}
        pageTitle="Edit Route"
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/EditRouteForm.js
 **/